import styled from 'styled-components';
import { ReactComponent as IconClose } from 'shared/assets/images/icons/iconClose.svg';
import { ReactComponent as IconEnlargeAttachment } from 'shared/assets/images/icons/iconEnlargeAttachment.svg';
import { ReactComponent as IconAttachmentPrevious } from 'shared/assets/images/icons/iconAttachmentPrevious.svg';
import { ReactComponent as IconAttachmentNext } from 'shared/assets/images/icons/iconAttachmentNext.svg';

export const AttachmentIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-text-base, #5a6a6a);
  line-height: 0;

  &.av-back-button {
    gap: 6px;
    padding: 4px 8px 4px 2px;
    line-height: 1.2;
    font-family: 'Montserrat', Arial, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-primary, #41aaaf);
    flex-shrink: 0;

    span {
      line-height: 1.2;
    }
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:hover svg {
    transform: scale(1.2);
  }

  &.av-back-button:hover svg {
    transform: none;
  }

  &:active svg {
    transform: scale(1);
  }

  svg {
    transition: all 0.2s ease-in-out;
  }
`;

export const StyledIconClose = styled(IconClose)`
  width: 20px;
  height: 20px;
  color: inherit;
`;

export const StyledIconEnlarge = styled(IconEnlargeAttachment)`
  width: 20px;
  height: 20px;
  color: var(--color-text-muted, #5a6a6a);
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.2);
  }

  &:active {
    transform: scale(1);
  }
`;

export const StyledIconPrevious = styled(IconAttachmentPrevious)`
  height: 16px;
  color: inherit;
`;

export const StyledIconNext = styled(IconAttachmentNext)`
  height: 16px;
  color: inherit;
`;

export const AttachmentViewerBackdrop = styled.div<{ $zIndex?: number }>`
  position: fixed;
  inset: 0;
  cursor: pointer;
  background: rgba(4, 42, 42, 0.35);
  z-index: ${({ $zIndex }) => ($zIndex ?? 1300) - 1};
`;

export const AttachmentViewerWrapper = styled.div<{
  $menuWidth?: number;
  $drawerOffset?: number;
  $zIndex?: number;
  $embedded?: boolean;
  $standaloneWidth?: number;
  $standaloneFullHeight?: boolean;
  $compactFit?: boolean;
  /** PWA review bottom sheet: natural height, never tuck under chrome. */
  $pwaSheet?: boolean;
  $centered?: boolean;
}>`
  background-color: var(--color-status-warning-bg, #fff5e2);
  border: 1px solid var(--color-border, #dbeae3);
  display: flex;
  flex-direction: column;
  font-family: 'Inter', Arial, sans-serif;
  --av-viewer-width: ${({ $standaloneWidth }) => $standaloneWidth ?? 670}px;
  ${({
    $embedded,
    $menuWidth,
    $drawerOffset,
    $zIndex,
    $standaloneFullHeight,
    $compactFit,
    $pwaSheet,
    $centered,
  }) => {
    if (!$embedded) {
      if ($centered) {
        return `
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(var(--av-viewer-width), calc(100vw - 48px));
  max-width: calc(100vw - 48px);
  height: calc(100vh - 64px);
  max-height: calc(100vh - 64px);
  z-index: ${$zIndex ?? 1300};
  box-sizing: border-box;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(4, 42, 42, 0.18);
  `;
      }
      return `
  position: fixed;
  top: 135px;
  right: ${$drawerOffset || 0}px;
  width: var(--av-viewer-width);
  max-width: calc(100vw - ${$menuWidth || 0}px - ${$drawerOffset || 0}px);
  height: calc(100vh - 79px);
  max-height: calc(100vh - 79px);
  z-index: ${$zIndex ?? 1000};
  box-sizing: border-box;
  `;
    }
    if ($compactFit) {
      return `
  position: relative;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  flex: 1 1 auto;
  `;
    }
    if ($pwaSheet) {
      // Bottom-sheet review: content height only. Do not use margin-top: -30px —
      // that tucks the viewer under the chrome and clips the close X into a "^".
      return `
  position: relative;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  border-radius: 0;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 0 0 auto;
  height: auto;
  min-height: 0;
  max-height: none;
  `;
    }
    if ($standaloneFullHeight) {
      // Edit drawer: height follows preview content; cap at drawer viewport, scroll when taller.
      return `
  position: relative;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 0 0 auto;
  height: auto;
  min-height: 500px;
  max-height: calc(100vh - 132px);
  `;
    }
    return `
  position: relative;
  width: 100%;
  min-height: 410px;
  max-height: 610px;
  margin-top: -30px;
  border-radius: 8px;
  overflow: hidden;
  .av-preview-area {
    margin-bottom: 0;
  }
  `;
  }}
  .av-centered-content {
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .av-transaction-reference {
    font-family: 'Inter', Arial, sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    text-align: center;
    margin-bottom: 8px;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--color-text-dark, #18302b);
  }
  .av-no-attachment-text {
    color: var(--color-text-muted, #5a6a6a);
    font-family: 'Montserrat', Arial, sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
    text-align: center;
    margin-bottom: 32px;
  }
  .av-top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 16px;
    background: var(--color-surface-raised, #fff);
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    border-bottom: 1px solid var(--color-border, #e5e5e5);
    width: 100%;
    position: relative;
    color: var(--color-text-base, #2e2e2e);
    box-sizing: border-box;
    gap: 8px;
    min-width: 0;
  }
  .av-top-bar-left {
    font-family: 'Inter';
    font-weight: 600;
    font-size: 12px;
    line-height: 24px;
    letter-spacing: 0;
    color: var(--color-text-base, #2e2e2e);
    min-width: 0;
  }
  .av-top-bar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
    min-width: 0;
  }
  .av-file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .av-file-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e5f6fb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: #3cb3b7;
    font-size: 14px;
  }
  .av-file-name {
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text-base, #2e2e2e);
  }
  .av-previewing-text {
    font-family: 'Montserrat', Arial, sans-serif;
    font-weight: 550;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
    color: var(--color-text-dark, #042a2a);
    opacity: 0.7;
    display: flex;
    align-items: center;
    text-align: right;
    white-space: nowrap;
  }
  .av-toolbar {
    background: var(--color-primary, #41aaaf);
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 48px;
    padding: 0 10px 0 16px;
    color: #fff;
    font-size: 15px;
    gap: 16px;
    width: 100%;
  }
  .av-toolbar-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0;
    margin-right: 0;
    font-weight: 600;
    font-size: 18px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  }
  .av-toolbar-icon.no-margin {
    margin-left: 0;
    margin-right: 0;
  }
  .av-toolbar-svg {
    width: 24px;
    height: 24px;
    display: block;
  }
  .av-toolbar-svg-trash {
    width: 18px;
    height: 18px;
  }
  .av-preview-area {
    flex: 0 1 auto;
    background: var(--color-surface-raised, #fff);
    margin: 16px;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    width: calc(100% - 32px);
    min-height: 200px;
    max-height: calc(100vh - 270px);
  }
  .av-pdf-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0;
    box-sizing: border-box;
    background-color: var(--color-surface-raised, #fff);
    min-height: 200px;
    max-height: calc(100vh - 270px);
    .react-pdf__Page {
      margin-bottom: 0;
      border-bottom: 10px solid var(--color-border, #f0f0f0);
      &:last-child {
        border-bottom: none;
      }
    }
    .react-pdf__Page__canvas {
      max-width: 100%;
      height: auto !important;
    }
  }
  .av-pdf-zoom-container .react-pdf__Page__canvas {
    max-width: none;
  }
  .av-toolbar-icon-group {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .av-upload-button-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-family: 'Inter';
    font-weight: 400;
    font-size: 13px;
    line-height: 24px;
    letter-spacing: 0;

    span {
      margin-bottom: 2px;
    }
  }
  ${({ $embedded, $standaloneFullHeight, $compactFit, $pwaSheet }) =>
    $compactFit
      ? `
  .av-top-bar,
  .av-toolbar {
    flex-shrink: 0;
    height: 40px;
  }
  .av-toolbar {
    padding: 0 8px 0 12px;
    gap: 8px;
  }
  .av-preview-area {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    max-height: none !important;
    height: auto !important;
    margin: 6px !important;
    width: calc(100% - 12px) !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .av-pdf-container {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    max-height: none !important;
    height: 100% !important;
    width: 100% !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
  }
  `
      : $pwaSheet
      ? `
  .av-top-bar,
  .av-toolbar {
    flex-shrink: 0;
  }
  .av-preview-area {
    flex: 0 0 auto !important;
    min-height: 0 !important;
    max-height: 132px !important;
    height: 132px !important;
    margin: 8px !important;
    width: calc(100% - 16px) !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .av-preview-area--loading {
    align-items: center !important;
    justify-content: center !important;
    background: var(--color-surface, #f3f7f8);
  }
  .av-pdf-container {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    max-height: 132px !important;
    height: 100% !important;
    width: 100% !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
  }
  .av-pdf-zoom-container.av-pdf-tap-expand {
    max-height: 100%;
    overflow: hidden !important;
  }
  `
      : $embedded && $standaloneFullHeight
      ? `
  .av-centered-content {
    height: auto !important;
    min-height: 160px;
    padding: 24px 16px;
  }
  .av-top-bar,
  .av-toolbar {
    flex-shrink: 0;
  }
  .av-preview-area {
    flex: 1 1 auto !important;
    min-height: calc(500px - 112px) !important;
    max-height: calc(100vh - 228px) !important;
    height: auto !important;
    margin: 8px !important;
    width: calc(100% - 16px) !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .av-pdf-container {
    flex: 0 0 auto !important;
    min-height: 0 !important;
    max-height: calc(100vh - 244px) !important;
    height: auto !important;
    width: 100% !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    overflow-x: auto !important;
    overflow-y: auto !important;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    overscroll-behavior: contain;
  }
  .av-pdf-container .av-pdf-zoom-container {
    flex: 0 0 auto !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }
  `
      : ''}
`;
